import * as React from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import { useTheme } from "@mui/joy/styles";
import MDEditor from "@uiw/react-md-editor";

import { ReadingNote } from "../../api";

export interface ReadingNoteFormValues {
  title: string;
  content: string;
}

interface Props {
  initial?: Partial<ReadingNote>;
  onSubmit: (data: ReadingNoteFormValues) => void | Promise<void>;
  submitLabel?: string;
  title?: string;
  loading?: boolean;
}

const defaults: ReadingNoteFormValues = {
  title: "",
  content: "",
};

export default function AdminReadingNoteForm({
  initial,
  onSubmit,
  submitLabel = "Save",
  title: formTitle,
  loading = false,
}: Props) {
  const theme = useTheme();
  const [values, setValues] = React.useState<ReadingNoteFormValues>({
    ...defaults,
    ...initial,
    content: initial?.content ?? "",
  });

  const handleChange = (field: keyof ReadingNoteFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const payload = {
      title: values.title.trim(),
      content: values.content.trim(),
    };
    onSubmit(payload);
  };

  const canSubmit = values.title.trim().length > 0 && values.content.trim().length > 0;

  return (
    <Stack
      component="form"
      spacing={2.5}
      onSubmit={handleSubmit}
      sx={{ width: "100%" }}
    >
      {formTitle ? (
        <Stack spacing={0.75}>
          <Typography level="title-md" sx={{ fontWeight: 700 }}>
            {formTitle}
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            Craft a thoughtful headline and devotional content. Markdown formatting is supported for structure and emphasis.
          </Typography>
        </Stack>
      ) : null}

      <FormControl required>
        <FormLabel>Title</FormLabel>
        <Input
          value={values.title}
          onChange={(event) => handleChange("title", event.target.value)}
          placeholder="E.g. The joy of morning mangala arati"
          autoComplete="off"
        />
        <FormHelperText>
          This title appears in the articles library and on pinned devotional callouts.
        </FormHelperText>
      </FormControl>

      <FormControl required>
        <FormLabel>Content</FormLabel>
        <Box
          data-color-mode={theme.palette.mode === "dark" ? "dark" : "light"}
          sx={{
            borderRadius: "lg",
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            "& .w-md-editor": {
              boxShadow: "none",
              borderRadius: 0,
            },
            "& .w-md-editor-toolbar": {
              borderBottom: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          <MDEditor
            value={values.content}
            onChange={(value) => handleChange("content", value ?? "")}
            preview="edit"
            height={360}
          />
        </Box>
        <FormHelperText>
          Share scripture highlights, seva stories, or reflections. Encourage devotees with clarity and warmth.
        </FormHelperText>
      </FormControl>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
        <Button
          type="submit"
          size="md"
          loading={loading}
          disabled={!canSubmit || loading}
          sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, fontWeight: 600 }}
        >
          {submitLabel}
        </Button>
        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
          Every article nurtures the community’s daily study. Thank you for sharing your realizations.
        </Typography>
      </Stack>
    </Stack>
  );
}
